import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Receipt, Eye, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { DetailDialog, DetailRow } from "@/components/ui/detail-dialog";
import { UserName } from "@/components/common/EntityName";
import { paymentsApi } from "@/lib/api";
import type { Payment } from "@/lib/api/types";
import { fmtCurrency, fmtNumber, fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

const STATUS_FILTERS = ["ALL", "COMPLETED", "PENDING", "PROCESSING", "FAILED", "CANCELLED"] as const;

const STATUS_VARIANT: Record<string, "success" | "warning" | "destructive" | "muted"> = {
  COMPLETED: "success",
  PENDING: "warning",
  PROCESSING: "warning",
  INITIATED: "warning",
  FAILED: "destructive",
  CANCELLED: "muted",
  REFUNDED: "muted",
  EXPIRED: "muted",
};

export default function TransactionsList() {
  const [filter, setFilter] = useState<(typeof STATUS_FILTERS)[number]>("ALL");
  const [detail, setDetail] = useState<Payment | null>(null);

  const summary = useQuery({
    queryKey: ["payments", "summary"],
    queryFn: () => paymentsApi.summary().then((r) => r.data.data),
  });

  const {
    page, setPage, items, totalItems, totalPages, isFetching,
  } = usePagedQuery<Payment, { status: string }>({
    key: ["payments", filter],
    fetcher: paymentsApi.adminAll,
    extra: { status: filter },
  });

  const s = summary.data;
  const cards = [
    { label: "Collected", value: s ? fmtCurrency(s.completedAmount, "TZS") : "…", icon: CheckCircle2, cls: "text-success" },
    { label: "Completed", value: s ? fmtNumber(s.completedCount) : "…", icon: CheckCircle2, cls: "text-success" },
    { label: "In flight", value: s ? fmtNumber(s.pendingCount) : "…", icon: Clock, cls: "text-warning" },
    { label: "Failed", value: s ? fmtNumber(s.failedCount) : "…", icon: XCircle, cls: "text-destructive" },
  ];

  return (
    <>
      <PageHeader
        title="Transactions"
        subtitle={`${totalItems} mobile-money payments across the platform.`}
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((c) => (
          <Card key={c.label}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{c.label}</span>
                <c.icon className={`size-4 ${c.cls}`} />
              </div>
              <p className="mt-2 text-xl font-extrabold">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap gap-1.5 border-b border-border p-3">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => { setFilter(f); setPage(0); }}
                className={`cursor-pointer rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                  filter === f
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-card text-muted-foreground hover:text-foreground"
                }`}
              >
                {f === "ALL" ? "All" : f.charAt(0) + f.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title={isFetching ? "Loading…" : "No transactions"}
              description="No payments match this filter yet."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Payer</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead className="text-right">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => setDetail(t)}>
                    <TableCell className="font-mono text-xs">{t.referenceId}</TableCell>
                    <TableCell className="text-sm"><UserName id={t.userId} /></TableCell>
                    <TableCell className="text-sm">{t.provider}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{t.paymentType ?? "—"}</TableCell>
                    <TableCell className="text-sm font-semibold">{fmtCurrency(t.amount, t.currency || "TZS")}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[t.status] ?? "muted"}>{t.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.initiatedAt ? fmtRelativeTime(t.initiatedAt) : "—"}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" onClick={() => setDetail(t)}>
                        <Eye className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <DetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        icon={Receipt}
        title={detail ? fmtCurrency(detail.amount, detail.currency || "TZS") : ""}
        subtitle={detail?.referenceId}
      >
        {detail && (
          <>
            <div className="mb-2 flex justify-center">
              <Badge variant={STATUS_VARIANT[detail.status] ?? "muted"}>{detail.status}</Badge>
            </div>
            <DetailRow label="Payer" value={<UserName id={detail.userId} />} />
            <DetailRow label="Phone" value={detail.phoneNumber ?? "—"} />
            <DetailRow label="Provider" value={detail.provider} />
            <DetailRow label="Payment type" value={detail.paymentType ?? "—"} />
            <DetailRow label="For" value={detail.description ?? detail.entityId ?? "—"} />
            <DetailRow label="External ref" value={detail.externalTransactionId ?? "—"} />
            <DetailRow label="Initiated" value={detail.initiatedAt ? fmtRelativeTime(detail.initiatedAt) : "—"} />
            <DetailRow label="Completed" value={detail.completedAt ? fmtRelativeTime(detail.completedAt) : "—"} />
            {detail.failureReason && <DetailRow label="Failure reason" value={detail.failureReason} />}
          </>
        )}
      </DetailDialog>
    </>
  );
}
